<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <d-row
      no-gutters
      class="page-header py-4">
      <d-col
        col
        sm="6"
        class="text-center text-sm-left mb-4 mb-sm-0">
        <span class="text-uppercase page-subtitle">
          {{ $t('container.containersOrders') }}
        </span>
        <h3 class="page-title">
          {{ $t('container.reservation.containersReservationsList') }}
        </h3>
      </d-col>
    </d-row>

    <d-row
      style="width: 100%;"
      class="d-flex ml-0 mb-3 page-filters">
      <validation-observer
        ref="containers-reservations-filters-form"
        tag="form"
        style="width: 100%;"
        class="d-flex">
        <d-input
          :placeholder="$t('order.enterOrderNumber')"
          v-model="filters.order" />

        <v-select
          class="ml-2 mr-2 w-50"
          :options="productOptions"
          v-model="filters.product_id"
          :placeholder="$t('product.selectProduct')"
          :searchable="true"
          label="text"
          :reduce="product => product.value">
          <template slot="no-options">
            {{ $t('product.noProductsMatchingGivenPhrase') }}&hellip;
          </template>
        </v-select>

        <v-select
          class="ml-2 mr-2 w-50"
          :options="containerOptions"
          v-model="filters.container_id"
          :placeholder="$t('container.selectContainer')"
          :searchable="true"
          label="text"
          :reduce="container => container.value">
          <template slot="no-options">
            {{ $t('container.noContainersMatchingPhraseFound') }}&hellip;
          </template>
          <template slot="option" slot-scope="option">
            <div class="d-center">
              {{ option.text }} ({{ option.date }})
            </div>
          </template>
          <template slot="selected-option" slot-scope="option">
            <div class="selected d-center">
              {{ option.text }} ({{ option.date }})
            </div>
          </template>
        </v-select>

        <v-select
          class="ml-2 mr-2"
          :options="containerStatusOptions"
          v-model="filters.container_status"
          placeholder=""
          :searchable="false"
          label="text"
          style="min-width: 240px;"
          :clearable="false"
          :reduce="containerStatus => containerStatus.value">
        </v-select>

        <d-button
          theme="info"
          outline
          class="text-nowrap ml-2"
          @click.prevent="validateFiltersForm">
          {{ $t('ui.search') }}
        </d-button>

        <d-button
          theme="danger"
          outline
          class="text-nowrap ml-2"
          @click.prevent="clearFilters">
          {{ $t('ui.resetFilters') }}
        </d-button>
      </validation-observer>
    </d-row>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th scope="col" class="border-0">{{ $t('product.product') }}</th>
            <th scope="col" class="border-0" style="width: 300px;">{{ $t('ui.order') }}</th>
            <th scope="col" class="border-0" style="width: 300px">{{ $t('container.container') }}</th>
            <th scope="col" class="border-0" style="width: 150px;">{{ $t('ui.amount') }}</th>
          </tr>
        </thead>
        <tbody v-if="!loadError">
          <tr
            v-for="(reservation, index) of reservations"
            :key="'reservation-' + index">
            <td class="align-middle">
              {{ reservation.product.name }}<br>
              <small>{{ $t('product.productCode') }}: <strong>{{ reservation.product.code }}</strong></small>
            </td>
            <td class="align-middle">
              <template v-if="reservation.order_product">
                <router-link :to="'/zamowienia/edycja/' + reservation.order_product.order.id + '/z-rezerwacji'">
                  {{ reservation.order_product.order.order_name }}
                </router-link>
              </template>
            </td>
            <td class="align-middle">
              <router-link :to="'/kontenery/edycja/' + reservation.container.id + '/z-rezerwacji'">
                {{ reservation.container.number }}
              </router-link>
              <br>
              <small>
                {{ $t('ui.deliveryDate') }}:
                <strong>
                  {{ reservation.container.date }}
                </strong>
              </small>
            </td>
            <td class="align-middle">
              {{ reservation.container_quantity }} {{ $t('ui.piecesAbbr') }}
            </td>
          </tr>
          <tr v-if="reservations.length === 0">
            <td
              colspan="8"
              class="text-center">
              {{ $t('container.reservation.noContainersReservationsToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded"
        class="pb-3 pt-3 text-center overlay-loading">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning">
        {{ $t('ui.dataLoadingFailed') }} <a href="javascript:window.location.reload();" class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import HasFilters from './../mixins/HasFilters.vue';
import HasPagination from './../mixins/HasPagination.vue';
import ListUtils from './../utils/ListUtils.js';
import FormUtils from './../utils/FormUtils.js';
import Pagination from './../components/common/Pagination.vue';

export default {
  name: 'containers-reservations',
  mixins: [
    HasFilters,
    HasPagination
  ],
  components: {
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedContainers && this.loadedProducts;
    },
    productOptions () {
      let options = [];

      for (let i = 0; i < this.products.length; i++) {
        let option = {
          text: this.products[i].code,
          value: this.products[i].id
        };

        options.push(option);
      }

      return options;
    },
    containerOptions () {
      let options = [];

      for (let i = 0; i < this.containers.length; i++) {
        let option = {
          text: this.containers[i].number,
          value: this.containers[i].id,
          date: this.containers[i].date
        };

        options.push(option);
      }

      return options;
    },
    containerStatusOptions () {
      return [
        {
          text: this.$t('container.reservation.reservationsOpen'),
          value: 'OPEN'
        },
        {
          text: this.$t('container.reservation.reservationsClose'),
          value: 'CLOSED'
        }
      ];
    }
  },
  data() {
    return {
      reservations: [],
      loaded: false,
      loadedContainers: false,
      loadedProducts: false,
      loadError: false,
      containers: [],
      products: [],
      filters: {
        order: '',
        container_id: null,
        container_status: 'OPEN',
        product_id: null
      }
    };
  },
  mounted () {
    this.loadFilteredData();
    this.loadAdditionalData();
  },
  methods: {
    debouncedLoadFilteredData: debounce(function () {
      this.$bus.$emit('view-filters-save');
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = {};

      if (this.filters.order) {
        where.order = this.filters.order;
      }

      if (this.filters.product_id) {
        where.product_id = this.filters.product_id;
      }

      if (this.filters.container_id) {
        where.container_id = this.filters.container_id;
      }

      if (this.filters.container_status) {
        where.container_status = this.filters.container_status;
      }

      ListUtils.loadPaginatedItemsData(this, {
        method: 'get',
        endpoint: '/products/containers/reservations?page=' + this.currentPage,
        listField: 'reservations',
        params: {
          where
        }
      });
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/all-items',
        loadedKey: 'loadedProducts',
        outputKey: 'products',
        errorKey: 'loadError'
      });

      FormUtils.loadAdditionalData(this, {
        method: 'get',
        endpoint: '/products/containers/items?return=get&where[status]=OPEN',
        loadedKey: 'loadedContainers',
        outputKey: 'containers',
        errorKey: 'loadError'
      });
    },
    clearFilters () {
      this.loaded = false;
      this.loadError = false;
      this.filters = {
        order: '',
        container_id: null,
        container_status: 'OPEN',
        product_id: null
      };

      this.$bus.$emit('view-filters-reset');
      this.$bus.$emit('pagination-reset', false);
    },
    validateFiltersForm () {
      this.$bus.$emit('pagination-reset');
      FormUtils.validate(this.$refs['containers-reservations-filters-form'], this.debouncedLoadFilteredData);
    }
  }
};
</script>
